import React, { useState } from 'react';
import { FiMail, FiMapPin, FiMessageCircle, FiSend } from 'react-icons/fi';
import MarketingLayout from '../components/MarketingLayout';
import { supabase } from '../supabaseClient';

export default function ContactPage() {
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    async function handleSubmit(event) {
        event.preventDefault();
        setLoading(true);
        setError(null);

        const formData = new FormData(event.currentTarget);
        const inquiry = {
            name: formData.get('name'),
            email: formData.get('email'),
            mobile: formData.get('mobile') || null,
            restaurant_name: formData.get('restaurant') || null,
            topic: formData.get('topic'),
            message: formData.get('message'),
            status: 'new',
        };

        const { error: insertError } = await supabase
            .from('contact_inquiries')
            .insert(inquiry);

        setLoading(false);

        if (insertError) {
            console.error('Contact form error:', insertError);
            setError('Something went wrong. Please try again or email us directly.');
            return;
        }

        setSent(true);
        event.target.reset();
    }

    return (
        <MarketingLayout>
            <main className="mx-auto grid max-w-7xl gap-8 px-4 py-16 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
                <section>
                    <p className="section-title">Contact Us</p>
                    <h1 className="mt-3 text-5xl font-black tracking-tight">Let's talk about your restaurant setup</h1>
                    <p className="mt-4 text-sm font-medium leading-6 text-slate-500">
                        Ask about QR setup, table count, subscription plans, menu migration, or anything else you need before launching.
                    </p>
                    <div className="mt-8 grid gap-4">
                        {[
                            [<FiMail />, 'Email', <a href="mailto:contact@getyourqr.in" className="text-primary font-bold hover:underline">contact@getyourqr.in</a>],
                            [<FiMessageCircle />, 'WhatsApp', <a href="https://wa.me/919770511893?text=Hi%2C%20I%20need%20help%20with%20GetYourQR" target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline">+91 97705 11893 →</a>],
                            [<FiMapPin />, 'Location', 'Sudama Nagar Indore, Madhya Pradesh 452009'],
                        ].map(([icon, title, text]) => (
                            <div key={title} className="card flex items-center gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-xl text-charcoal">{icon}</div>
                                <div>
                                    <h2 className="font-black">{title}</h2>
                                    <p className="text-sm font-medium text-slate-500">{text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="card">
                    <h2 className="text-2xl font-black">Send a message</h2>
                    <p className="mt-2 text-sm font-medium text-slate-500">We'll get back to you within 24 hours.</p>
                    {sent && (
                        <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
                            ✅ Your message has been sent! We'll reach out to you soon.
                        </div>
                    )}
                    {error && (
                        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                            {error}
                        </div>
                    )}
                    <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
                        <input className="input-field" name="name" placeholder="Your name" required />
                        <input className="input-field" name="email" type="email" placeholder="Email address" required />
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">+91</span>
                            <input
                                className="input-field pl-12"
                                name="mobile"
                                type="tel"
                                placeholder="Mobile number"
                                pattern="[6-9][0-9]{9}"
                                title="Enter a valid 10-digit Indian mobile number"
                                required
                            />
                        </div>
                        <input className="input-field" name="restaurant" placeholder="Restaurant or cafe name" />
                        <select className="input-field" name="topic" defaultValue="setup">
                            <option value="setup">QR menu setup</option>
                            <option value="pricing">Plans and pricing</option>
                            <option value="support">Product support</option>
                            <option value="demo">Request a demo</option>
                        </select>
                        <textarea className="input-field min-h-36 resize-none" name="message" placeholder="Tell us what you need" required />
                        <button className="btn-primary justify-center flex items-center gap-2" disabled={loading}>
                            {loading ? 'Sending...' : <><FiSend /> Submit request</>}
                        </button>
                    </form>
                </section>
            </main>
        </MarketingLayout>
    );
}
